import { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCreateCell } from '../hooks/useCells';
import { usePacks } from '../hooks/usePacks';
import Button from '../components/Button';
import Input from '../components/Input';
import Select from '../components/Select';
import SearchBar from '../components/SearchBar';
import Card from '../components/Card';

export default function NewCell() {
  const navigate = useNavigate();
  const createCell = useCreateCell();
  const { data: packsData } = usePacks({ limit: 100 });

  const [formData, setFormData] = useState({
    cell_id: '',
    batch_id: '',
    received_box: '',
    shipment_id: '',
    received_date: new Date().toISOString().split('T')[0],
    cell_capacity_kwh: '',
    cell_type: 'Prabal1000',
    terminal_type: '',
    status: 'In Stock' as 'In Stock' | 'Reserved' | 'Used' | 'Scrapped',
    pack_id: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [packSearch, setPackSearch] = useState('');
  const [showPackList, setShowPackList] = useState(false);
  const packDropdownRef = useRef<HTMLDivElement>(null);

  const allPacks = packsData?.items || [];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (packDropdownRef.current && !packDropdownRef.current.contains(event.target as Node)) {
        setShowPackList(false);
      }
    };

    if (showPackList) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPackList]);

  const filteredPacks = useMemo(() => {
    if (!packSearch.trim()) return allPacks;
    const searchLower = packSearch.toLowerCase();
    return allPacks.filter(
      (pack) =>
        pack.pack_id?.toLowerCase().includes(searchLower) ||
        pack.batch_id?.toLowerCase().includes(searchLower) ||
        pack.customer?.toLowerCase().includes(searchLower) ||
        pack.customer_location?.toLowerCase().includes(searchLower)
    );
  }, [allPacks, packSearch]);

  const selectedPack = allPacks.find((pack) => pack.pack_id === formData.pack_id);

  const statusOptions = [
    { value: 'In Stock', label: 'In Stock' },
    { value: 'Reserved', label: 'Reserved' },
    { value: 'Used', label: 'Used' },
    { value: 'Scrapped', label: 'Scrapped' },
  ];
  
  const typeOptions = [
    { value: 'Prabal1000', label: 'Prabal1000' },
    { value: 'Prabal2000', label: 'Prabal2000' },
    { value: 'Prabal3000', label: 'Prabal3000' },
    { value: 'Prabal4000', label: 'Prabal4000' },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (!formData.cell_id.trim()) {
      setErrors({ cell_id: 'Cell ID is required' });
      return;
    }

    try {
      await createCell.mutateAsync({
        cell_id: formData.cell_id.trim(),
        batch_id: formData.batch_id.trim() || undefined,
        received_box: formData.received_box.trim() || undefined,
        shipment_id: formData.shipment_id.trim() || undefined,
        received_date: formData.received_date ? `${formData.received_date}T00:00:00.000Z` : undefined,
        cell_capacity_kwh: formData.cell_capacity_kwh ? parseFloat(formData.cell_capacity_kwh) : undefined,
        cell_type: formData.cell_type?.trim() || undefined,
        terminal_type: formData.terminal_type?.trim() || undefined,
        status: formData.status,
        pack_id: formData.pack_id || undefined,
      });
      navigate(`/cells/${formData.cell_id}`);
    } catch (error) {
      setErrors({ submit: 'Failed to create cell. Please try again.' });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
        <div className="flex items-center gap-3 w-full md:w-auto">
          <button
            onClick={() => navigate('/cells')}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors bg-white shadow-sm border border-gray-200"
            aria-label="Back to cells"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl md:text-4xl font-extrabold text-gray-900">
              New Cell
            </h1>
            <p className="text-sm md:text-lg text-gray-500 font-medium mt-1">
              Add a new cell to the inventory
            </p>
          </div>
        </div>
      </div>

      <Card>
        <form onSubmit={handleSubmit} className="space-y-6">
          <Input
            label="Cell ID *"
            value={formData.cell_id}
            onChange={(e) => setFormData({ ...formData, cell_id: e.target.value })}
            error={errors.cell_id}
            placeholder="e.g., JEK10150AC2402260316"
            required
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Batch ID"
              value={formData.batch_id}
              onChange={(e) => setFormData({ ...formData, batch_id: e.target.value })}
              placeholder="e.g., 5B"
            />

            <Input
              label="Received Box"
              value={formData.received_box}
              onChange={(e) => setFormData({ ...formData, received_box: e.target.value })}
              placeholder="Box number"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Shipment ID"
              value={formData.shipment_id}
              onChange={(e) => setFormData({ ...formData, shipment_id: e.target.value })}
              placeholder="Shipment identifier"
            />

            <Input
              label="Received Date"
              type="date"
              value={formData.received_date}
              onChange={(e) => setFormData({ ...formData, received_date: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Cell Type"
              options={typeOptions}
              value={formData.cell_type || ''}
              onChange={(e) => setFormData({ ...formData, cell_type: e.target.value })}
            />

            <Input
              label="Terminal Type"
              value={formData.terminal_type || ''}
              onChange={(e) => setFormData({ ...formData, terminal_type: e.target.value })}
              placeholder="e.g., M6 Thread"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Cell Capacity (kWh)"
              type="number"
              step="0.001"
              value={formData.cell_capacity_kwh}
              onChange={(e) => setFormData({ ...formData, cell_capacity_kwh: e.target.value })}
              placeholder="e.g., 0.150"
            />

            <Select
              label="Status"
              options={statusOptions}
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value as typeof formData.status })}
            />
          </div>

          <div>
            <label className="block text-base font-semibold mb-2 text-gray-700">
              Assigned Pack (optional)
            </label>
            <div className="relative" ref={packDropdownRef}>
              <div
                onClick={() => setShowPackList(!showPackList)}
                className="touch-target w-full px-4 py-3 text-base border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white hover:border-gray-400 cursor-pointer flex items-center justify-between"
              >
                <span className={formData.pack_id ? 'text-gray-900' : 'text-gray-500'}>
                  {selectedPack
                    ? `${selectedPack.pack_id}${selectedPack.batch_id ? ` (${selectedPack.batch_id})` : ''}`
                    : 'Select a pack (optional)...'}
                </span>
                <svg
                  className={`w-5 h-5 text-gray-400 transition-transform ${showPackList ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>

              {showPackList && (
                <div className="absolute z-10 w-full mt-2 bg-white border border-gray-300 rounded-xl shadow-lg max-h-80 overflow-hidden">
                  <div className="p-3 border-b border-gray-200">
                    <SearchBar
                      placeholder="Search packs by ID, batch, or customer..."
                      value={packSearch}
                      onChange={(e) => setPackSearch(e.target.value)}
                      onClear={() => setPackSearch('')}
                    />
                  </div>
                  <div className="max-h-60 overflow-y-auto">
                    <button
                      type="button"
                      onClick={() => {
                        setFormData({ ...formData, pack_id: '' });
                        setShowPackList(false);
                        setPackSearch('');
                      }}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${!formData.pack_id ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                        }`}
                    >
                      None (No pack assigned)
                    </button>
                    {filteredPacks.length === 0 ? (
                      <div className="px-4 py-3 text-sm text-gray-500">
                        {packSearch ? 'No packs found matching your search' : 'No packs available'}
                      </div>
                    ) : (
                      filteredPacks.map((pack) => (
                        <button
                          key={pack.pack_id}
                          type="button"
                          onClick={() => {
                            setFormData({ ...formData, pack_id: pack.pack_id });
                            setShowPackList(false);
                            setPackSearch('');
                          }}
                          className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 ${formData.pack_id === pack.pack_id
                            ? 'bg-blue-50 text-blue-700 font-medium'
                            : 'text-gray-700'
                            }`}
                        >
                          <div className="font-medium">{pack.pack_id}</div>
                          {(pack.batch_id || pack.customer) && (
                            <div className="text-xs text-gray-500 mt-1">
                              {pack.batch_id && <span>Batch: {pack.batch_id}</span>}
                              {pack.batch_id && pack.customer && <span> • </span>}
                              {pack.customer && <span>Customer: {pack.customer}</span>}
                            </div>
                          )}
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
            {errors.pack_id && (
              <p className="mt-1 text-sm text-red-600">{errors.pack_id}</p>
            )}
          </div>

          {errors.submit && (
            <div className="bg-red-50 border-l-4 border-red-500 rounded-xl p-4">
              <p className="text-sm text-red-700">{errors.submit}</p>
            </div>
          )}

          <div className="flex gap-4">
            <Button
              type="submit"
              variant="primary"
              disabled={createCell.isPending}
            >
              {createCell.isPending ? 'Creating...' : 'Create Cell'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate('/cells')}
            >
              Cancel
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

